import React, { useState, useEffect } from 'react';
import { auth } from './firebase';

const BACKEND_URL = 'https://pharmdev-rider.onrender.com';

const s = {
  statCard: { backgroundColor: '#FFF', borderRadius: 12, padding: 18, border: '1px solid #E5E7EB' },
  row:      { display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #F3F4F6', fontSize: 14 },
};

function Badge({ text, bg, color }) {
  return <span style={{ backgroundColor: bg, color, padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600 }}>{text}</span>;
}

export default function ReconciliationTab() {
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState('');

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const token = await auth.currentUser.getIdToken();
      const res = await fetch(`${BACKEND_URL}/admin/reconciliation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Could not load reconciliation data');
      setData(json);
    } catch (e) {
      setError(e.message || 'Could not load reconciliation data');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h2 style={{ fontWeight: 700, margin: 0, fontSize: 20 }}>🧮 Reconciliation</h2>
          <p style={{ color: '#888', margin: '4px 0 0', fontSize: 13 }}>
            What Paystack actually holds vs. what PharmDev owes across every app — riders, pharmacies, refunds, and the customer wallet.
          </p>
        </div>
        <button style={{ padding: '9px 16px', backgroundColor: '#1D9E75', color: '#FFF', border: 'none', borderRadius: 10, cursor: 'pointer', fontWeight: 600, fontSize: 13 }} onClick={load}>
          ↻ Refresh
        </button>
      </div>

      {loading ? (
        <p style={{ color: '#888', textAlign: 'center', padding: 40 }}>Loading…</p>
      ) : error ? (
        <p style={{ color: '#EF4444', textAlign: 'center', padding: 40 }}>{error}</p>
      ) : (
        <>
          <div style={{
            ...s.statCard, marginBottom: 20,
            border: `2px solid ${data.solvent ? '#1D9E75' : '#EF4444'}`,
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          }}>
            <div>
              <div style={{ fontSize: 13, color: '#888' }}>Solvency check</div>
              <div style={{ fontSize: 22, fontWeight: 700 }}>
                Paystack balance GH₵ {data.paystackBalance.toFixed(2)} vs. total owed GH₵ {data.totalOwed.toFixed(2)}
              </div>
            </div>
            {data.solvent
              ? <Badge text="✅ Covered" bg="#E8F8F2" color="#1D9E75" />
              : <Badge text="⚠️ Shortfall" bg="#FEE2E2" color="#B91C1C" />}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 20 }}>
            <div style={s.statCard}>
              <div style={{ fontSize: 20, fontWeight: 700 }}>GH₵ {data.paystackBalance.toFixed(2)}</div>
              <div style={{ fontSize: 12, color: '#888' }}>Live Paystack balance</div>
            </div>
            <div style={s.statCard}>
              <div style={{ fontSize: 20, fontWeight: 700 }}>GH₵ {data.pendingRiderPharmacyPayouts.toFixed(2)}</div>
              <div style={{ fontSize: 12, color: '#888' }}>Owed to riders + pharmacies</div>
            </div>
            <div style={s.statCard}>
              <div style={{ fontSize: 20, fontWeight: 700 }}>GH₵ {data.pendingRefunds.toFixed(2)}</div>
              <div style={{ fontSize: 12, color: '#888' }}>Pending customer refunds</div>
            </div>
            <div style={s.statCard}>
              <div style={{ fontSize: 20, fontWeight: 700 }}>GH₵ {data.walletLiability.toFixed(2)}</div>
              <div style={{ fontSize: 12, color: '#888' }}>Total wallet liability</div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div style={s.statCard}>
              <h3 style={{ fontWeight: 700, margin: '0 0 10px', fontSize: 15 }}>Wallet ledger breakdown</h3>
              <div style={s.row}><span>Real top-ups (Paystack-verified)</span><b>+GH₵ {data.walletBreakdown.realTopups.toFixed(2)}</b></div>
              <div style={s.row}><span>Referral bonuses</span><b>+GH₵ {data.walletBreakdown.referralBonuses.toFixed(2)}</b></div>
              <div style={s.row}><span>Refunds credited</span><b>+GH₵ {data.walletBreakdown.refundsCredited.toFixed(2)}</b></div>
              <div style={s.row}><span>Withdrawals</span><b>−GH₵ {data.walletBreakdown.withdrawals.toFixed(2)}</b></div>
              <div style={s.row}><span>Spent at checkout</span><b>−GH₵ {data.walletBreakdown.spentAtCheckout.toFixed(2)}</b></div>
              <div style={{ ...s.row, borderBottom: 'none', fontWeight: 700 }}>
                <span>Expected total</span><span>GH₵ {data.walletExpectedFromLedger.toFixed(2)}</span>
              </div>
            </div>

            <div style={s.statCard}>
              <h3 style={{ fontWeight: 700, margin: '0 0 10px', fontSize: 15 }}>Wallet integrity check</h3>
              <div style={s.row}><span>Actual wallet balances (sum)</span><b>GH₵ {data.walletLiability.toFixed(2)}</b></div>
              <div style={s.row}><span>Expected from transaction log</span><b>GH₵ {data.walletExpectedFromLedger.toFixed(2)}</b></div>
              <div style={{ ...s.row, borderBottom: 'none' }}>
                <span>Difference</span>
                {Math.abs(data.walletDelta) < 0.01
                  ? <Badge text="✅ Matches" bg="#E8F8F2" color="#1D9E75" />
                  : <Badge text={`⚠️ GH₵ ${data.walletDelta.toFixed(2)} unexplained`} bg="#FEE2E2" color="#B91C1C" />}
              </div>
              <p style={{ fontSize: 12, color: '#888', marginTop: 10 }}>
                A non-zero difference means a wallet balance changed outside the normal top-up/withdraw/pay/refund/bonus paths — worth investigating directly in Firebase.
              </p>
              <p style={{ fontSize: 12, color: '#888', marginTop: 6 }}>
                {data.referralsPaidCount} referral bonus{data.referralsPaidCount === 1 ? '' : 'es'} paid to date.
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
